import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Checkbox, FormControl, InputLabel, Select, MenuItem, Divider } from '@mui/material';
import { useMutation, useQuery } from 'react-query';
import { CSVLink } from 'react-csv';
import client from '../../../API';
import CreatorContainers from '../Search/creatorcontainersearch';
import BlitzHeader from '../../../Components/BlitzHeader';
import { niches } from '../../../Utils/constants';
import { toast } from 'sonner';

const SimilarCreators = () => {
  const { creatorId } = useParams();
  const [creatorDetails, setCreatorDetails] = useState(null);
  const [similarCreators, setSimilarCreators] = useState([]);
  const [visibleCreators, setVisibleCreators] = useState(6);
  const [showPretty, setShowPretty] = useState(true);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [primaryMarket, setPrimaryMarket] = useState([]);

  const { data: creatorDetailsData, isLoading: loadingCreator, isError: isErrorCreator } = useQuery(
    ['creator', creatorId],
    () => client.creators.getCreator(creatorId),
    {
      onSuccess: (data) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('Creator details fetched:', data);
        }
        toast.success('Creator details loaded', {
          description: `Name: ${data?.display_name || 'Unknown'}`
        });
        setCreatorDetails(data);
        setPrimaryMarket(data.primary_markets||[]); // Set the initial primary market
      },
      onError: (error) => {
        toast.error('Failed to fetch creator details', {
          description: error.message
        });
      }
    }
  );

  const { data: allCreators, isLoading: loadingCreators } = useQuery(
    ['creators'],
    () => client.creators.getAllCreators(),
    {
      onSuccess: (data) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('All creators fetched:', data);
        }
        toast.success('Creators data loaded', {
          description: `${data?.length || 0} creators available`
        });
      },
      onError: (error) => {
        toast.error('Failed to fetch creators', {
          description: error.message
        });
      }
    }
  );

  const { mutate: fetchSimilarCreators } = useMutation(client.creators.list, {
    onSuccess: (data) => {
      const allCreators = data || [];
      if (process.env.NODE_ENV === 'development') {
        console.log('All creators fetched:', allCreators);
      }
      toast.success('Similar creators data loaded', {
        description: `${allCreators.length || 0} similar creators available`
      });

      const filteredCreators = allCreators.filter(creator => {
        const regionMatch = creator.region === creatorDetails?.region;
        const primaryMarketMatch = creator.primary_markets === primaryMarket;
        const rateMatch = creator.rate_tt_brand >= 0.5 * creatorDetails?.rate_tt_brand && creator.rate_tt_brand <= 1.5 * creatorDetails?.rate_tt_brand;

        if (process.env.NODE_ENV === 'development') {
          console.log(`Creator ${creator.creatorId}: regionMatch=${regionMatch}, primaryMarketMatch=${primaryMarketMatch}, rateMatch=${rateMatch}`);
        }
        return regionMatch && primaryMarketMatch && rateMatch;
      });

      // Randomize the filtered creators
      const randomizedCreators = filteredCreators.sort(() => 0.5 - Math.random());
      if (process.env.NODE_ENV === 'development') {
        console.log('Filtered and randomized creators:', randomizedCreators);
      }
      setSimilarCreators(randomizedCreators);
    },
    onError: (error) => {
      toast.error('Failed to fetch similar creators', {
        description: error.message
      });
      setSimilarCreators([]);
    },
  });

  useEffect(() => {
    if (creatorDetails?.region) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Fetching similar creators with region:', creatorDetails.region);
      }
      toast.info('Searching for similar creators', {
        description: `Region: ${creatorDetails.region}`
      });
      fetchSimilarCreators();
    }
  }, [creatorDetails, primaryMarket, fetchSimilarCreators]);

  const handleShowMore = () => {
    setVisibleCreators((prev) => prev + 6);
  };

  const toggleView = () => {
    setShowPretty(!showPretty);
  };

  const handleSelectItem = (creatorId) => {
    setSelectedItems(prevSelectedItems => {
      const newSelectedItems = new Set(prevSelectedItems);
      if (newSelectedItems.has(creatorId)) {
        newSelectedItems.delete(creatorId);
      } else {
        newSelectedItems.add(creatorId);
      }
      return newSelectedItems;
    });
  };

  const generateCsvData = () => {
    return similarCreators.map(creator => ({
      creator: creator.creator,
      primary_markets: creator.primary_markets,
      region: creator.region,
      rate_ig_reelbrand: creator.rate_ig_reelbrand,
      rate_tt_brand: creator.rate_tt_brand,
    }));
  };

  if (loadingCreator || loadingCreators) return 'Loading creator details...';
  if (isErrorCreator || !creatorDetails) return 'No creator details found';

  return (
    <>
      <BlitzHeader />
      <Box sx={{ mt: 10, mb: 4 }}>
        <Typography variant="h3">Similar Creators to {creatorDetails?.creator}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" sx={{ mr: 2 }}>
            You are looking at
          </Typography>
          <FormControl variant="outlined">
            <InputLabel id="primary-market-select-label">Primary Market</InputLabel>
            <Select
              labelId="primary-market-select-label"
              id="primary-market-select"
              value={primaryMarket}
              onChange={(e) => setPrimaryMarket(e.target.value)}
              label="Primary Market"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {niches.map((market) => (
                <MenuItem key={market} value={market}>
                  {market}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ mb: 4 }}>
          <Typography variant="body1">
            {/* Add creator details typography here */}
            Region: {creatorDetails.region}
          </Typography>
        </Box>
        <Divider sx={{ my: 2 }} />
        <Button onClick={toggleView}>
          {showPretty ? "Show CSV" : "Make it Pretty"}
        </Button>
        <Button>
          <CSVLink data={generateCsvData()} filename="similar_creators.csv" style={{ textDecoration: 'none', color: 'inherit' }}>
            Download CSV
          </CSVLink>
        </Button>
        {showPretty ? (
          <CreatorContainers
            creators={similarCreators.slice(0, visibleCreators)}
            platform="TikTok" // or any other platform you need to pass
            selectedItems={selectedItems}
            onCardClick={handleSelectItem}
          />
        ) : (
          <Box sx={{ overflowX: 'auto', mt: 2 }}>
            <Paper elevation={3}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">Select</TableCell>
                    <TableCell>Creator</TableCell>
                    <TableCell>Primary Market</TableCell>
                    <TableCell>Region</TableCell>
                    <TableCell>IG Reels Sound</TableCell>
                    <TableCell>IG Reels Brand</TableCell>
                    <TableCell>TikTok Sound</TableCell>
                    <TableCell>TikTok Brand</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {similarCreators.slice(0, visibleCreators).map((creator) => (
                    <TableRow key={creator.creatorId}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedItems.has(creator.creatorId)}
                          onChange={() => handleSelectItem(creator.creatorId)}
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>{creator.creator}</TableCell>
                      <TableCell>{creator.primary_markets}</TableCell>
                      <TableCell>{creator.region}</TableCell>
                      <TableCell>${creator.rate_ig_reelsound}</TableCell>
                      <TableCell>${creator.rate_ig_reelbrand}</TableCell>
                      <TableCell>${creator.rate_tt_sound}</TableCell>
                      <TableCell>${creator.rate_tt_brand}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Box>
        )}
        {visibleCreators < similarCreators.length && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button variant="contained" onClick={handleShowMore}>
              Show More
            </Button>
          </Box>
        )}
      </Box>
    </>
  );
};

export default SimilarCreators;